// src/common/ui/verify-all-overlay.js
// Unified overlay for verifyAll workflow across desktop and Word add-in

import { showInlineAsk } from '../ask.js';

/**
 * Ensures the verify-all overlay exists and returns its API
 * @returns {Object} Overlay API with show/hide/update methods
 */
export function ensureVerifyAllOverlay() {
  let overlay = document.getElementById('verify-all-overlay');
  if (overlay?.__api) return overlay.__api;

  overlay = document.createElement('div');
  overlay.id = 'verify-all-overlay';
  overlay.className = 'verify-all-overlay hidden';
  overlay.setAttribute('aria-hidden', 'true');
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Verify All Progress');
  
  overlay.innerHTML = `
    <div class="verify-all-card" role="document">
      <div class="verify-all-header">
        <h3>Verifying All Citations</h3>
        <div class="verify-all-header-actions">
          <button type="button" class="verify-all-minimize" title="Minimize" aria-label="Minimize overlay">−</button>
          <button type="button" class="verify-all-cancel" title="Cancel verification" aria-label="Cancel verification">Cancel All</button>
        </div>
      </div>
      
      <div class="verify-all-body">
        <!-- Stage Progress Section -->
        <div class="verify-all-stages">
          <div class="verify-stage" data-stage="parse">
            <div class="stage-header">
              <span class="stage-icon">⏳</span>
              <span class="stage-label">Parsing Citations</span>
              <span class="stage-count">0 / 0</span>
            </div>
            <div class="stage-progress">
              <div class="stage-progress-bar" style="width: 0%"></div>
            </div>
          </div>
          
          <div class="verify-stage" data-stage="resolve">
            <div class="stage-header">
              <span class="stage-icon">⏸️</span>
              <span class="stage-label">Resolving Sources</span>
              <span class="stage-count">0 / 0</span>
            </div>
            <div class="stage-progress">
              <div class="stage-progress-bar" style="width: 0%"></div>
            </div>
          </div>
          
          <div class="verify-stage" data-stage="analyze">
            <div class="stage-header">
              <span class="stage-icon">⏸️</span>
              <span class="stage-label">Checking Congruence</span>
              <span class="stage-count">0 / 0</span>
            </div>
            <div class="stage-progress">
              <div class="stage-progress-bar" style="width: 0%"></div>
            </div>
          </div>
        </div>
        
        <!-- Exclusions Section -->
        <div class="verify-all-exclusions hidden">
          <div class="exclusions-header">
            <span class="exclusion-icon">⚠️</span>
            <span class="exclusion-label">Not considered</span>
          </div>
          <div class="exclusions-note">Adjust filters to include these sources, then run Verify All again.</div>
          <ul class="exclusions-list"></ul>
        </div>

        <!-- Resolution Section -->
        <div class="verify-all-resolutions">
          <div class="resolutions-header">
            <span class="resolution-icon">📋</span>
            <span class="resolution-label">Resolution Required</span>
            <span class="resolution-badge">0</span>
          </div>
          <div class="resolutions-list" id="verify-all-resolutions-list">
            <!-- Resolution items will be injected here -->
          </div>
        </div>
        
        <!-- Completion Message -->
        <div class="verify-all-complete hidden">
          <div class="complete-icon">✓</div>
          <div class="complete-message">Verification complete!</div>
        </div>
      </div>
    </div>
  `;
  
  document.body.appendChild(overlay);
  
  // Get element references
  const card = overlay.querySelector('.verify-all-card');
  const minimizeBtn = overlay.querySelector('.verify-all-minimize');
  const cancelBtn = overlay.querySelector('.verify-all-cancel');
  const bodyEl = overlay.querySelector('.verify-all-body');
  const resolutionsList = overlay.querySelector('#verify-all-resolutions-list');
  const resolutionBadge = overlay.querySelector('.resolution-badge');
  const completeEl = overlay.querySelector('.verify-all-complete');
  const exclusionsSection = overlay.querySelector('.verify-all-exclusions');
  const exclusionsList = overlay.querySelector('.exclusions-list');
  
  // Stage elements
  const stages = {
    parse: overlay.querySelector('[data-stage="parse"]'),
    resolve: overlay.querySelector('[data-stage="resolve"]'),
    analyze: overlay.querySelector('[data-stage="analyze"]'),
  };
  
  // State
  let isMinimized = false;
  let cancelCallback = null;
  let resolutionCount = 0;
  let excludedTypeKeys = [];
  
  // Helper to show/hide overlay
  const showOverlay = () => {
    overlay.classList.remove('hidden');
    overlay.setAttribute('aria-hidden', 'false');
  };
  
  const hideOverlay = () => {
    overlay.classList.add('hidden');
    overlay.setAttribute('aria-hidden', 'true');
    isMinimized = false;
    card.classList.remove('minimized');
    minimizeBtn.textContent = '−';
    minimizeBtn.setAttribute('aria-label', 'Minimize overlay');
  };
  
  // Minimize/maximize toggle
  minimizeBtn.addEventListener('click', () => {
    isMinimized = !isMinimized;
    if (isMinimized) {
      card.classList.add('minimized');
      bodyEl.style.display = 'none';
      minimizeBtn.textContent = '□';
      minimizeBtn.setAttribute('aria-label', 'Maximize overlay');
    } else {
      card.classList.remove('minimized');
      bodyEl.style.display = 'block';
      minimizeBtn.textContent = '−';
      minimizeBtn.setAttribute('aria-label', 'Minimize overlay');
    }
  });
  
  // Cancel button
  cancelBtn.addEventListener('click', () => {
    if (cancelCallback) {
      cancelCallback();
    }
    // You could also add confirmation dialog here if needed
  });
  
  // Update stage progress
  const updateStage = (stageName, current, total, status = 'active') => {
    const stage = stages[stageName];
    if (!stage) return;
    
    const icon = stage.querySelector('.stage-icon');
    const count = stage.querySelector('.stage-count');
    const progressBar = stage.querySelector('.stage-progress-bar');
    
    count.textContent = `${current} / ${total}`;
    
    const percentage = total > 0 ? (current / total) * 100 : 0;
    progressBar.style.width = `${percentage}%`;
    
    // Update status
    stage.classList.remove('pending', 'active', 'complete');
    stage.classList.add(status);
    
    // Update icon based on status
    if (status === 'pending') {
      icon.textContent = '⏸️';
    } else if (status === 'active') {
      icon.textContent = '⏳';
    } else if (status === 'complete') {
      icon.textContent = '✓';
    }
  };

  const setExcludedTypes = (types = []) => {
    excludedTypeKeys = Array.from(new Set(types.filter(Boolean)));
    exclusionsList.innerHTML = '';
    if (excludedTypeKeys.length === 0) {
      exclusionsSection.classList.add('hidden');
      return;
    }

    for (const typeKey of excludedTypeKeys) {
      const item = document.createElement('li');
      item.className = 'exclusion-item';
      item.textContent = typeKey;
      exclusionsList.appendChild(item);
    }

    exclusionsSection.classList.remove('hidden');
  };
  
  // Add resolution item
  const addResolutionItem = (footnoteId, citeId, question, questionId, clientId, documentId, apiBase, databaseManager, footnotesManager) => {
    // Check if this question already exists
    const existing = resolutionsList.querySelector(`[data-question-id="${CSS.escape(questionId)}"]`);
    if (existing) {
      console.log('[VerifyAllOverlay] Resolution item already exists for question:', questionId);
      return existing;
    }
    
    console.log('[VerifyAllOverlay] Creating resolution item for:', { footnoteId, citeId, questionId });
    
    // Look up citation text from footnotesManager if available
    let citeText = null;
    if (citeId && footnotesManager) {
      try {
        // Use getCitationById method if available
        if (typeof footnotesManager.getCitationById === 'function') {
          const citation = footnotesManager.getCitationById(footnoteId, citeId);
          if (citation && citation.source && citation.source.raw) {
            citeText = citation.source.raw;
            console.log(`[VerifyAllOverlay] Found citation text via getCitationById:`, citeText.substring(0, 100));
          }
        } else {
          // Fallback to direct lookup
          const footnote = footnotesManager.footnotesCache?.find(f => String(f.id) === String(footnoteId));
          if (footnote && footnote.cached_citations) {
            const citation = footnote.cached_citations.find(c => c.id === citeId);
            if (citation && citation.source && citation.source.raw) {
              citeText = citation.source.raw;
              console.log(`[VerifyAllOverlay] Found citation text:`, citeText.substring(0, 100));
            }
          }
        }
      } catch (e) {
        console.error('[VerifyAllOverlay] Failed to look up citation text:', e);
      }
    }
    
    // Create container for this specific resolution
    const item = document.createElement('div');
    item.className = 'resolution-item';
    item.dataset.footnoteId = footnoteId;
    item.dataset.citeId = citeId;
    item.dataset.questionId = questionId;
    
    // Add a header to show context
    const header = document.createElement('div');
    header.className = 'resolution-item-header';
    
    // Build header text with citation text if available
    let headerText = `Citation ${footnoteId}`;
    if (citeId) {
      headerText += `, Citation ${citeId}`;
      if (citeText) {
        const maxLength = 80;
        const truncated = citeText.length > maxLength ? citeText.substring(0, maxLength) + '...' : citeText;
        headerText += `: "${truncated}"`;
      }
    }
    
    header.innerHTML = `
      <span class="resolution-context">${headerText}</span>
    `;
    item.appendChild(header);
    
    // Create a fake card structure that showInlineAsk expects
    // IMPORTANT: Use a unique ID combining footnote and question to avoid conflicts
    const uniqueCardId = `${footnoteId}-q${questionId}`;
    const fakeCard = document.createElement('div');
    fakeCard.className = 'card overlay-fake-card';
    fakeCard.dataset.id = uniqueCardId;
    
    // Create card body
    const cardBody = document.createElement('div');
    cardBody.className = 'card-body show';
    
    // Create results container
    const resultsContainer = document.createElement('div');
    resultsContainer.className = 'results';
    
    cardBody.appendChild(resultsContainer);
    fakeCard.appendChild(cardBody);
    
    // Wrap in a container
    const askContainer = document.createElement('div');
    askContainer.className = 'resolution-ask-container';
    askContainer.appendChild(fakeCard);
    
    item.appendChild(askContainer);
    resolutionsList.appendChild(item);
    
    // Increment counter
    resolutionCount++;
    resolutionBadge.textContent = String(resolutionCount);
    
    // Show resolutions section if hidden
    const resolutionsSection = overlay.querySelector('.verify-all-resolutions');
    resolutionsSection.classList.remove('hidden');
    
    // Ensure the overlay body can scroll and show the new item
    // Use requestAnimationFrame to ensure DOM is fully updated before scrolling
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Scroll the item into view within the overlay body
        const bodyEl = overlay.querySelector('.verify-all-body');
        if (bodyEl && item) {
          // Force the body to recognize its scrollable area
          bodyEl.style.overflowY = 'auto';
          
          // Scroll to the new item
          item.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
          
          // Also ensure the body scrolls to show it
          const itemRect = item.getBoundingClientRect();
          const bodyRect = bodyEl.getBoundingClientRect();
          
          // If item is below visible area, scroll it into view
          if (itemRect.bottom > bodyRect.bottom) {
            bodyEl.scrollTop += (itemRect.bottom - bodyRect.bottom) + 20; // 20px padding
          }
        }
      });
    });
    
    // Inject the inline ask into the fake card structure
    try {
      // Pass the unique card ID that matches what we created above
      showInlineAsk(
        askContainer,
        uniqueCardId, // This matches the data-id we set on the fake card
        citeId,
        question,
        questionId,
        clientId,
        documentId,
        apiBase,
        databaseManager,
        footnotesManager
      );
    } catch (e) {
      console.error('[VerifyAllOverlay] Failed to inject inline ask:', e);
      console.error('[VerifyAllOverlay] Error details:', {
        footnoteId,
        citeId,
        questionId,
        uniqueCardId,
        cardExists: !!askContainer.querySelector('.card')
      });
    }
    
    return item;
  };
  
  // Remove resolution item (when answered)
  const removeResolutionItem = (questionId) => {
    const item = resolutionsList.querySelector(`[data-question-id="${CSS.escape(questionId)}"]`);
    if (item) {
      item.classList.add('resolved');
      setTimeout(() => {
        item.remove();
        resolutionCount = Math.max(0, resolutionCount - 1);
        resolutionBadge.textContent = String(resolutionCount);
        
        // Hide section if no more resolutions
        if (resolutionCount === 0) {
          const resolutionsSection = overlay.querySelector('.verify-all-resolutions');
          resolutionsSection.classList.add('hidden');
        }
      }, 300);
    }
  };
  
  // Show completion state
  const showComplete = () => {
    // Hide stages
    overlay.querySelector('.verify-all-stages').style.display = 'none';
    
    // Show completion message
    completeEl.classList.remove('hidden');
    
    // Update cancel button to "Close"
    cancelBtn.textContent = 'Close';
    cancelBtn.onclick = () => hideOverlay();
    
    // Auto-hide after delay if no resolutions pending
    if (resolutionCount === 0) {
      setTimeout(() => {
        hideOverlay();
      }, 2000);
    }
  };
  
  // Reset overlay to initial state
  const reset = () => {
    // Reset stages
    Object.keys(stages).forEach(stageName => {
      updateStage(stageName, 0, 0, 'pending');
    });
    
    // Clear resolutions
    resolutionsList.innerHTML = '';
    resolutionCount = 0;
    resolutionBadge.textContent = '0';
    
    // Hide/show appropriate sections
    overlay.querySelector('.verify-all-stages').style.display = 'block';
    overlay.querySelector('.verify-all-resolutions').classList.add('hidden');
    completeEl.classList.add('hidden');
    exclusionsSection.classList.add('hidden');
    exclusionsList.innerHTML = '';
    excludedTypeKeys = [];
    
    // Reset cancel button
    cancelBtn.textContent = 'Cancel All';
    cancelBtn.onclick = () => {
      if (cancelCallback) cancelCallback();
    };
    
    // Reset minimized state
    if (isMinimized) {
      minimizeBtn.click();
    }
  };
  
  // Public API
  const api = {
    show() {
      reset();
      showOverlay();
    },
    
    hide: hideOverlay,
    
    updateStage(stageName, current, total, status = 'active') {
      updateStage(stageName, current, total, status);
    },
    
    setExcludedTypes(types) {
      setExcludedTypes(types);
    },

    addResolution(footnoteId, citeId, question, questionId, clientId, documentId, apiBase, databaseManager, footnotesManager) {
      return addResolutionItem(footnoteId, citeId, question, questionId, clientId, documentId, apiBase, databaseManager, footnotesManager);
    },
    
    removeResolution(questionId) {
      removeResolutionItem(questionId);
    },
    
    showComplete() {
      showComplete();
    },
    
    onCancel(callback) {
      cancelCallback = callback;
    },
    
    isVisible() {
      return !overlay.classList.contains('hidden');
    },
    
    isMinimized() {
      return isMinimized;
    },
    
    getResolutionCount() {
      return resolutionCount;
    }
  };
  
  overlay.__api = api;
  return api;
}
